import { useState, type ReactNode } from "react";
import { pairClaims, type OwnerNewParticipantsResponse, type OwnerWaiting, type ParentClaim } from "@teamna/shared";
import { useAskParents, useLinkParentClaim, useRejectParentClaim } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { messageForError } from "../components/CodeFlow.tsx";
import { Button, TextAreaField, showToast } from "../components/index.ts";
import { WhatsAppIcon } from "../components/PhoneSignIn.tsx";
import { phoneText } from "./families/model.ts";
import { daysFor } from "./new-participants-days.ts";

/**
 * Step 4 of `/owner/new-participants`: Ask the parents (link-parents-sync, migration 0045).
 *
 * The owner sends one message in the parents' WhatsApp group from their own WhatsApp, as the group
 * admin. Teamna writes it: the owner's text, then the login line and the find link, which Teamna
 * always adds. A parent opens the link, signs in with the number they use in the group, picks their
 * child and types their name. The answers come back here: Link or No. Teamna sends nothing.
 */

/**
 * The default text: what the business did, the few children it could not match, and what a parent
 * does next. The owner posts it in their own group, so "we" is the business. `participants` is the
 * business word ("swimmers").
 */
export function defaultGroupMessage(participants: string): string {
  return `Dear parents,\n\nWe added all our ${participants} and their parents to *Teamna*, the app we now use for our schedule and sessions. However, we could not match a few children to their parent, and we need your help.\n\nPlease open the link below and find your child. If you have more than one child with us, add all your children in one answer. It takes one minute. We check each answer, then we link each child to you. If a child is linked to you already, the page tells you.`;
}

export const REMINDER_MESSAGE =
  "Dear parents,\n\nSome children still have no parent in Teamna. If you did not find your child yet, please open the link below. It takes one minute. If your child is linked to you already, the page tells you.";

/** Teamna adds these lines under the owner's text, so the login rule and the link are always there. */
export const LOGIN_LINE = "📱 *Please use the same mobile number you use in this group to log in.*";

export function fullGroupMessage(text: string, findUrl: string): string {
  return `${text.trim()}\n\n${LOGIN_LINE}\n\n${findUrl}`;
}

/** WhatsApp's own marks on one line: *bold*, _italic_, ~strike~. Text only, never HTML. */
function waLine(line: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /\*([^*\n]+)\*|(^|\s)_([^_\n]+)_|~([^~\n]+)~/g;
  let last = 0;
  let n = 0;
  for (const match of line.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > last) out.push(line.slice(last, start));
    if (match[1] !== undefined) out.push(<b key={`${key}-${n++}`}>{match[1]}</b>);
    else if (match[3] !== undefined) out.push(match[2] ?? "", <i key={`${key}-${n++}`}>{match[3]}</i>);
    else out.push(<s key={`${key}-${n++}`}>{match[4]}</s>);
    last = start + match[0].length;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}

export function WhatsAppText({ text }: { text: string }) {
  const lines = text.split("\n");
  return (
    <>
      {lines.map((line, i) => (
        // oxlint-disable-next-line react/no-array-index-key -- lines of one message, in order
        <span key={i} className="block min-h-[1em]">
          {waLine(line, String(i))}
        </span>
      ))}
    </>
  );
}

/** Open answers by waiting name. */
export function claimsByName(claims: ParentClaim[]): Map<string, ParentClaim[]> {
  const out = new Map<string, ParentClaim[]>();
  for (const claim of claims) {
    if (claim.status !== "new") continue;
    for (const id of claim.pendingIds) out.set(id, [...(out.get(id) ?? []), claim]);
  }
  return out;
}

/**
 * The open answers that clash, and the two-parent answers with their partners: a mother and a
 * father on one child are no clash, Link both puts them in one family (rule in `@teamna/shared`).
 */
export function claimPairs(claims: ParentClaim[]) {
  const open = claims.filter((c) => c.status === "new");
  const { clash, partners } = pairClaims(open);
  const byId = new Map(open.map((c) => [c.id, c]));
  /**
   * The answers Link both links with this one: its pair, and the other answers of the same two
   * people. The earliest answer first: it makes the family, the other person joins it.
   */
  const groupOf = (claim: ParentClaim): ParentClaim[] => {
    if (!partners.has(claim.id)) return [];
    const seen = new Set([claim.id]);
    for (const id of seen) for (const other of partners.get(id) ?? []) seen.add(other);
    return [...seen]
      .map((id) => byId.get(id))
      .filter((c): c is ParentClaim => !!c)
      .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt) || a.id.localeCompare(b.id));
  };
  return { isClash: (claim: ParentClaim) => clash.has(claim.id), groupOf };
}
export type ClaimPairs = ReturnType<typeof claimPairs>;

export function relationWord(claim: ParentClaim): string | null {
  return claim.relation === "mother" ? "Mother" : claim.relation === "father" ? "Father" : claim.relation === "other" ? "Other" : null;
}

/** Link and No on answers, with the days of each name (the coach's days, else every day of the level). */
export function useClaimActions(businessId: string, waiting: OwnerWaiting[]) {
  const { label } = useLabels();
  const link = useLinkParentClaim(businessId);
  const reject = useRejectParentClaim(businessId);
  const [busy, setBusy] = useState(false);
  const byId = new Map(waiting.map((w) => [w.id, w]));
  const weekdaysOf = (claim: ParentClaim) => Object.fromEntries(claim.pendingIds.map((id) => [id, byId.get(id) ? daysFor(byId.get(id)!) : []]));
  const first = (name: string) => name.split(" ")[0] ?? name;

  const linkOne = (claim: ParentClaim) =>
    link.mutate(
      { id: claim.id, weekdays: weekdaysOf(claim) },
      {
        // The names the server linked, not the names of the answer (review F1.2).
        onSuccess: (result) =>
          showToast(
            result.secondAdult && result.linked > 0
              ? `${first(claim.name)} is a second ${label("member")} in that family now. ${result.linkedNames.map(first).join(" and ")} joined the family too.`
              : result.secondAdult
              ? `${first(claim.name)} is a second ${label("member")} of ${claim.names.map(first).join(" and ")} now.`
              : result.linked === 0
              ? `${first(claim.name)} is already in that family.`
              : `Linked ${result.linkedNames.map(first).join(" and ")} to ${first(claim.name)}.${
                  result.skippedNames.length ? ` ${result.skippedNames.map(first).join(" and ")} ${result.skippedNames.length > 1 ? "have" : "has"} a ${label("member")} now.` : ""
                }`,
          ),
        onError: (error) => showToast(messageForError(error), { tone: "info" }),
      },
    );

  /** "Dania and Omar have a parent in another family.": the names a link left where they were. */
  const skippedText = (skipped: string[]) =>
    skipped.length ? ` ${skipped.map(first).join(" and ")} ${skipped.length > 1 ? "have" : "has"} a ${label("member")} in another family.` : "";

  /**
   * Link all: one answer at a time. When an answer of a pair fails, the other answers of that pair
   * that did not run yet wait: the bulk link never links one parent of a pair alone (review F3.2).
   */
  const linkMany = async (claims: ParentClaim[], pairs: ClaimPairs) => {
    setBusy(true);
    let names = 0;
    let problem: unknown = null;
    const skipped: string[] = [];
    const done = new Set<string>();
    const held = new Set<string>();
    let waited = 0;
    for (const claim of claims) {
      if (held.has(claim.id)) {
        waited += 1;
        continue;
      }
      try {
        // oxlint-disable-next-line no-await-in-loop -- one answer at a time: each link changes the families the next one reads
        const result = await link.mutateAsync({ id: claim.id, weekdays: weekdaysOf(claim) });
        names += result.linked;
        skipped.push(...result.skippedNames);
        done.add(claim.id);
      } catch (error) {
        problem = error;
        for (const other of pairs.groupOf(claim)) if (!done.has(other.id)) held.add(other.id);
      }
    }
    setBusy(false);
    const rest = skippedText([...new Set(skipped)]);
    showToast(
      problem
        ? `Linked ${names}. Some answers could not be linked: ${messageForError(problem)}${waited > 1 ? ` ${waited} other answers of a pair wait too.` : waited ? " The other answer of that pair waits too." : ""}${rest}`
        : `Linked ${names} ${names === 1 ? label("participant") : label("participant", { plural: true })} from the answers.${rest}`,
      problem ? { tone: "info" } : {},
    );
  };

  /**
   * Two parents: the first answer makes the family, the other person becomes its second adult. When
   * the server cannot add the other person (a family of their own), it says which names stay where
   * they are; the toast says so too, never "one family" (review F6.1).
   */
  const linkBoth = async (claims: ParentClaim[]) => {
    setBusy(true);
    try {
      const skipped: string[] = [];
      for (const claim of claims) {
        // oxlint-disable-next-line no-await-in-loop -- in order: the second answer joins the family the first one makes
        skipped.push(...(await link.mutateAsync({ id: claim.id, weekdays: weekdaysOf(claim) })).skippedNames);
      }
      const parents = [...new Set(claims.map((c) => first(c.name)))].join(" and ");
      const children = [...new Set(claims.flatMap((c) => c.names))].map(first);
      showToast(
        skipped.length
          ? `Linked the answers of ${parents}, but not as one family.${skippedText([...new Set(skipped)])} Check them on the Families page.`
          : `${parents} are one family now, with ${children.join(" and ")}.`,
        skipped.length ? { tone: "info" } : {},
      );
    } catch (error) {
      showToast(messageForError(error), { tone: "info" });
    } finally {
      setBusy(false);
    }
  };

  const no = (claim: ParentClaim) =>
    reject.mutate(claim.id, {
      onSuccess: () => showToast(`You said no to ${first(claim.name)}. ${claim.names.map(first).join(" and ")} ${claim.names.length > 1 ? "wait" : "waits"} as before.`),
      onError: (error) => showToast(messageForError(error), { tone: "info" }),
    });

  return { linkOne, linkMany, linkBoth, no, busy: busy || link.isPending || reject.isPending };
}

export function AskStep({
  businessId,
  businessName,
  data,
  onNext,
}: {
  businessId: string;
  businessName: string;
  data: OwnerNewParticipantsResponse;
  onNext: () => void;
}) {
  const { label } = useLabels();
  const ask = useAskParents(businessId);
  const [text, setText] = useState<string | null>(null);
  const message = text ?? defaultGroupMessage(label("participant", { plural: true }));
  const posted = data.askParents.postedAt !== null;
  const [editing, setEditing] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const toFind = data.waiting.filter((w) => !w.participant).length;
  const pairs = claimPairs(data.claims);
  const actions = useClaimActions(businessId, data.waiting);
  const open = data.claims.filter((c) => c.status === "new");
  const clean = open.filter((c) => c.pendingIds.length > 0 && !pairs.isClash(c));
  const parents = label("member", { plural: true });

  const copy = async () => {
    if (!message.trim()) {
      showToast("Write a message first.", { tone: "info" });
      return;
    }
    try {
      await navigator.clipboard.writeText(fullGroupMessage(message, data.askParents.findUrl));
    } catch {
      // Only a real copy counts as asked (review F2.2): the owner copies the text by hand, then says so.
      setCopyFailed(true);
      showToast("Copy did not work here. Select the text below and copy it.", { tone: "info" });
      return;
    }
    record();
    showToast("Copied. Paste it in the WhatsApp group and send it.");
  };
  const record = () => {
    ask.mutate("post", { onError: (error) => showToast(messageForError(error), { tone: "info" }) });
    setEditing(false);
    setCopyFailed(false);
  };

  return (
    <section className="grid items-start gap-4 2xl:grid-cols-[minmax(0,1fr)_300px]" aria-labelledby="ask-title">
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-col gap-3 rounded-card border border-line bg-paper p-4">
          <h2 id="ask-title" className="text-[17px] font-bold text-ink">
            Ask the {parents} to find their child
          </h2>
          <p className="text-[13.5px] text-ink-2">
            You send one message in the group from your own WhatsApp, as the group admin. Teamna writes it for you. A {label("member")} opens the link, signs in
            with the number they use in this group, taps their child, and types their own name. You check each answer before it links.
          </p>
          {posted && !editing ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-confirmed-surface px-2.5 py-0.5 text-[12px] font-semibold text-confirmed-ink">Message copied</span>
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                Copy it again
              </Button>
            </div>
          ) : (
            <>
              <TextAreaField label="Message to the group" rows={6} value={message} onChange={(e) => setText(e.target.value)} />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[12.5px] text-ink-2">Teamna adds the login line and the link at the end. *bold* and _italic_ work like in WhatsApp.</span>
                <Button variant="ghost" size="sm" onClick={() => setText(null)}>
                  Use the default text
                </Button>
              </div>
              {copyFailed ? (
                <div className="flex flex-col gap-2">
                  <TextAreaField label="The message to copy" rows={8} readOnly value={fullGroupMessage(message, data.askParents.findUrl)} onFocus={(e) => e.target.select()} />
                  <div>
                    <Button variant="secondary" size="sm" loading={ask.isPending} onClick={record}>
                      I copied it
                    </Button>
                  </div>
                </div>
              ) : null}
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="whatsapp" disabled={toFind === 0} loading={ask.isPending} onClick={() => void copy()}>
                  <WhatsAppIcon className="size-4" />
                  Copy the message
                </Button>
                <span className="text-[12.5px] text-ink-2">
                  Paste it in your {parents}' WhatsApp group and send it. {toFind} {label("participant", { plural: true })} on the list.
                </span>
              </div>
            </>
          )}
        </div>

        {posted || data.claims.length > 0 ? (
          <div className="flex flex-col rounded-card border border-line bg-paper">
            <div className="flex flex-wrap items-start justify-between gap-2 p-4">
              <div className="flex flex-col gap-1">
                <h3 className="text-[16px] font-bold text-ink">Answers</h3>
                <p className="text-[13px] text-ink-2">
                  {open.length
                    ? `A mother and a father on one child go in one family. Any other mix of ${parents} on one child is a clash. Pick one, or link both to one family.`
                    : data.claims.length
                      ? "All answers checked."
                      : "No answers yet."}
                </p>
              </div>
              {clean.length ? (
                <Button size="sm" loading={actions.busy} onClick={() => void actions.linkMany(clean, pairs)}>
                  Link all without a clash
                </Button>
              ) : null}
            </div>
            <ul aria-label="Answers">
              {data.claims.map((claim) => (
                <ClaimRow key={claim.id} claim={claim} pairs={pairs} actions={actions} />
              ))}
            </ul>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button variant={posted ? "primary" : "secondary"} onClick={onNext}>
            {posted ? "Next: still missing" : "Skip to still missing"}
          </Button>
          <span className="text-[12.5px] text-ink-2">
            {data.waiting.length} {label("participant", { plural: true })} without a {label("member")}.
          </span>
        </div>
      </div>

      <WhatsAppPreview businessName={businessName} message={message} findUrl={data.askParents.findUrl} sent={posted && !editing} />
    </section>
  );
}

/**
 * The group message as WhatsApp draws a message from someone else: a flat white bubble with a small
 * tail, the sender in colour, links in blue and the time in the corner. No gloss: it is a preview of
 * what the parents see, not a Teamna control.
 */
function WhatsAppPreview({ businessName, message, findUrl, sent }: { businessName: string; message: string; findUrl: string; sent: boolean }) {
  return (
    <div className="overflow-hidden rounded-card border border-line" aria-label="Preview of the group message">
      <div className="flex items-center gap-2.5 border-b border-line bg-paper px-3 py-2">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-sunken text-[13px] font-semibold text-ink-2" aria-hidden="true">
          {businessName.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 leading-tight">
          <p className="truncate text-[15px] font-medium text-wa-ink">{businessName} parents</p>
          <p className="text-[12.5px] text-wa-time">{sent ? "As your group sees it" : "Preview, not sent yet"}</p>
        </div>
      </div>
      <div className="bg-wa-chat px-3 py-4">
        <div className="relative max-w-[92%] rounded-lg rounded-tl-none bg-wa-in px-2 pt-1.5 pb-1 text-[14.2px] leading-[19px] text-wa-ink" dir="auto">
          {/* WhatsApp's tail for a message from someone else: on the left, flush with the top corner. */}
          <svg className="absolute -top-px -left-2 text-wa-in" width="8" height="13" viewBox="0 0 8 13" aria-hidden="true">
            <path fill="currentColor" d="M1.533 3.568 8 12.193V1H2.812C1.042 1 .474 2.156 1.533 3.568z" />
          </svg>
          <p className="text-[12.8px] font-medium text-wa-name">You · admin</p>
          <WhatsAppText text={message} />
          <span className="block h-[19px]" />
          <WhatsAppText text={LOGIN_LINE} />
          <span className="block h-[19px]" />
          <span className="block break-all text-wa-link">{findUrl}</span>
          <span className="float-right -mb-0.5 ml-3 pt-1 text-[11px] text-wa-time">now</span>
          <span className="clear-both block" />
        </div>
      </div>
    </div>
  );
}

export function ClaimRow({ claim, pairs, actions }: { claim: ParentClaim; pairs: ClaimPairs; actions: ReturnType<typeof useClaimActions> }) {
  const { label } = useLabels();
  const clash = claim.status === "new" && pairs.isClash(claim);
  const group = claim.status === "new" ? pairs.groupOf(claim) : [];
  const relation = relationWord(claim);
  const from =
    claim.source === "coach" && claim.coachName ? `from ${label("staff")} ${claim.coachName.split(" ")[0]}` : relation;
  const detail = [
    from,
    phoneText(claim.phoneE164),
    claim.whatsappName && claim.whatsappName !== claim.name ? `WhatsApp: ${claim.whatsappName}` : claim.whatsappName ? null : "typed their own name",
  ]
    .filter(Boolean)
    .join(" · ");
  const taken = claim.status === "new" && claim.pendingIds.length === 0;
  return (
    <li className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-2.5">
      <div className="min-w-0 flex-1 text-[13.5px]">
        <p className="font-semibold text-ink">
          {claim.name} → {claim.names.join(", ")}
        </p>
        <p className="text-[12.5px] text-ink-2">{detail}</p>
        {claim.note ? (
          <p className="mt-1 text-[13px] [overflow-wrap:anywhere] whitespace-pre-line text-ink">
            Note: <bdi dir="auto">{claim.note}</bdi>
          </p>
        ) : null}
      </div>
      {claim.status === "linked" ? (
        <span className="rounded-full bg-confirmed-surface px-2.5 py-0.5 text-[12px] font-semibold text-confirmed-ink">Linked</span>
      ) : claim.status === "rejected" ? (
        <span className="rounded-full bg-sunken px-2.5 py-0.5 text-[12px] font-semibold text-ink-2">Not linked</span>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {taken ? <span className="rounded-full bg-late-surface px-2.5 py-0.5 text-[12px] font-semibold text-late-ink">Child has a {label("member")} now</span> : null}
          {clash ? <span className="rounded-full bg-cancelled-surface px-2.5 py-0.5 text-[12px] font-semibold text-cancelled">Clash</span> : null}
          {group.length ? (
            <>
              <span className="rounded-full bg-accent-surface px-2.5 py-0.5 text-[12px] font-semibold text-accent-strong">Two {label("member", { plural: true })}</span>
              <Button size="sm" loading={actions.busy} onClick={() => void actions.linkBoth(group)}>
                Link both
              </Button>
            </>
          ) : (
            <Button size="sm" variant={taken ? "secondary" : "primary"} loading={actions.busy} onClick={() => actions.linkOne(claim)}>
              {taken ? `Add as second ${label("member")}` : "Link"}
            </Button>
          )}
          <Button size="sm" variant="ghost" disabled={actions.busy} onClick={() => actions.no(claim)}>
            No
          </Button>
        </div>
      )}
    </li>
  );
}
